import { isEqual } from "lodash";
import { AnyAction } from "redux";
import { put, all, call, takeLatest, select, delay } from "redux-saga/effects";

import {
  ApiService,
  PWTokenRefreshPair,
  PWTokenObtainPair,
  PasswordChange,
  PasswordReset,
  PasswordResetConfirm,
  User,
  QRAuthObtainPair,
  MFASend,
  MFALoginSend,
  MFAVerify,
} from "pwapi";

import { OpenAPI } from "pwapi/core/OpenAPI";

import { JwtResponse } from "custom-models";

import {
  ConfirmResetPasswordAction,
  loginSuccess,
  tokenRetrieved,
} from "../actions/authActions";
import {
  LOGIN_REQUEST,
  LOGIN_FAILURE,
  TOKEN_REQUEST,
  TOKEN_PAIR_REQUEST,
  TOKEN_FAILURE,
  PW_RESET_REQUEST,
  PW_RESET_SUCCESS,
  PW_RESET_FAILURE,
  PW_RESET_CONFIRM_REQUEST,
  PW_RESET_CONFIRM_SUCCESS,
  PW_RESET_CONFIRM_FAILURE,
  USER_DETAIL_REQUEST,
  USER_DETAIL_SUCCESS,
  USER_DETAIL_FAILURE,
  SAVE_PROFILE_REQUEST,
  SET_PROFILE_SUCCESS,
  SET_PROFILE_FAILURE,
  SET_PROFILE_SKIP,
  AUTHORIZED_SET_PASSWORD_SUCCESS,
  AUTHORIZED_SET_PASSWORD_FAILURE,
  LOGIN_BARCODE,
  MFA_GET_CODE_REQUEST,
  MFA_GET_CODE_SUCCESS,
  MFA_GET_CODE_FAILURE,
  MFA_VERIFY_CODE_REQUEST,
  MFA_VERIFY_CODE_SUCCESS,
  MFA_VERIFY_CODE_FAILURE,
  MFA_LOGIN_GET_CODE_REQUEST,
} from "../actions/constants";
import { addToast, removeToast, nextId } from "../actions/toastActions";
import { AuthState } from "../reducers/authReducer";
import { getAuthCookie, setAuthCookie } from "../utils/cookie";

/////////////////////////
// Login Functionality //
/////////////////////////

function* logIn(action: { type: string; payload: PWTokenObtainPair }) {
  try {
    const response: JwtResponse = yield call(
      ApiService.destroyPwTokenObtainPair,
      {
        requestBody: action.payload,
      }
    );

    yield put(loginSuccess(response));
  } catch (e: any) {
    yield put({
      type: LOGIN_FAILURE,
      error:
        !e.body ||
        ((!e.body.non_field_errors || e.body.non_field_errors.length === 0) &&
          !e.body.detail),
      payload: e,
    });
  }
}

function* loginWithToken(action: AnyAction) {
  const { token, pin } = action.payload;
  try {
    const requestBody: QRAuthObtainPair = {
      token,
      pin,
    };
    // @ts-ignore
    const response = yield call(ApiService.destroyQrAuthObtainPair, {
      requestBody,
    });

    yield put(loginSuccess(response));
  } catch (e: any) {
    yield put({
      type: LOGIN_FAILURE,
      error:
        !e.body ||
        ((!e.body.non_field_errors || e.body.non_field_errors.length === 0) &&
          !e.body.detail),
      payload: e,
    });
  }
}

/////////////////////////////
// JWT Token Functionality //
/////////////////////////////

// The JWT Refresh Token is not stored in state, instead it is
// stored on the OpenAPI object like OpenAPI.TOKEN.
// The auto-generated OpenAPI module doesn't use REFRESH_TOKEN
// so we have to manually build this request body here.
function getRefreshRequestBody(): PWTokenRefreshPair {
  return {
    access: typeof OpenAPI.TOKEN === "string" ? OpenAPI.TOKEN : "",
    refresh: OpenAPI.REFRESH_TOKEN || "",
  };
}

function* refreshAccessToken() {
  try {
    const response: JwtResponse = yield call(ApiService.destroyPwTokenRefresh, {
      requestBody: getRefreshRequestBody(),
    });
    yield put(tokenRetrieved(response));
  } catch (e) {
    yield put({ type: TOKEN_FAILURE, error: true, payload: e });
  }
}

function* refreshAccessTokenPair() {
  try {
    const response: JwtResponse = yield call(
      ApiService.destroyPwTokenRefreshPair,
      { requestBody: getRefreshRequestBody() }
    );
    yield put(tokenRetrieved(response));
  } catch (e) {
    yield put({ type: TOKEN_FAILURE, error: true, payload: e });
  }
}

///////////////////////
// MFA Functionality //
///////////////////////

function* mfaLoginGetCode(action: {
  type: string;
  payload: {
    mfaMethod: MFALoginSend.mfa_method;
    username: string;
  };
}) {
  try {
    const requestBody: MFALoginSend = {
      mfa_method: action.payload.mfaMethod,
      username: action.payload.username,
    };
    const response: MFASend = (yield call(ApiService.destroyMfaLoginSend, {
      requestBody,
    })) as MFASend;
    yield put({
      type: MFA_GET_CODE_SUCCESS,
      payload: response,
      requestKey: "mfa-get-code",
      requestSuccess: true,
    });
  } catch (e: any) {
    yield put({
      type: MFA_GET_CODE_FAILURE,
      error: false,
      payload: e,
      requestKey: "mfa-get-code",
      requestFailure: true,
    });
    yield put(
      addToast(
        {
          kind: "danger",
          title: "Authentication Code Error",
          message: `There was a problem requesting your authentication code. ${e.body?.detail}`,
        },
        nextId()
      )
    );
  }
}

function* mfaGetCode(action: {
  type: string;
  payload: {
    mfaMethod: MFASend.mfa_method;
  };
}) {
  try {
    const requestBody: MFASend = {
      mfa_method: action.payload.mfaMethod,
    };
    const response: MFASend = yield call(ApiService.destroyMfaSend, {
      requestBody,
    });
    yield put({
      type: MFA_GET_CODE_SUCCESS,
      payload: response,
      requestKey: "mfa-get-code",
      requestSuccess: true,
    });
  } catch (e: any) {
    yield put({
      type: MFA_GET_CODE_FAILURE,
      error: false,
      payload: e,
      requestKey: "mfa-get-code",
      requestFailure: true,
    });
    yield put(
      addToast(
        {
          kind: "danger",
          title: "Authentication Code Error",
          message: `There was a problem requesting your authentication code. ${e.body?.detail}`,
        },
        nextId()
      )
    );
  }
}

function* mfaVerifyCode(action: {
  type: string;
  payload: {
    mfaMethod: MFAVerify.mfa_method;
    mfaCode: string;
  };
}) {
  try {
    const requestBody: MFAVerify = {
      mfa_method: action.payload.mfaMethod,
      mfa_code: action.payload.mfaCode,
    };
    const response: MFAVerify = yield call(ApiService.destroyMfaVerify, {
      requestBody,
    });
    const cookie = getAuthCookie();
    cookie.mfaSetupRequired = false;
    setAuthCookie(cookie);
    yield put({
      type: MFA_VERIFY_CODE_SUCCESS,
      payload: response,
      requestKey: "mfa-verify-code",
      requestSuccess: true,
    });
  } catch (e) {
    yield put({
      type: MFA_VERIFY_CODE_FAILURE,
      error: false,
      payload: e,
      requestKey: "mfa-verify-code",
      requestFailure: true,
    });
  }
}

//////////////////////////////////
// Password Reset Functionality //
//////////////////////////////////

/*
 * Pulls elements from action.payload.response.new_password1 and new_password2
 * indicating any specific problems with the password validation.
 * Errors from /api/auth/password/reset/confirm/ contain this information;
 * this method is imported and used successfully by passwordResetConfirmReducer.
 * Unfortunately errors from /api/auth/password/change/ only contain
 * the 400 and a statusText of "Bad Request".
 */
function extractPasswordIssues(payload: any) {
  const { body } = payload;
  let errors = new Array<string>();
  if (body?.new_password1) {
    // Is this one needed? Always comes back empty
    errors = errors.concat(body.new_password1 as Array<string>);
  }
  if (body?.new_password2) {
    // Password errors (e.g. ["Too similar to your other stuff", "Not enough characters"] etc.)
    errors = errors.concat(body.new_password2 as Array<string>);
  }
  return errors;
}

function* resetPassword(action: { type: string; payload: PasswordReset }) {
  try {
    const result: PasswordReset = yield call(ApiService.destroyPasswordReset, {
      requestBody: action.payload,
    });
    yield put({ type: PW_RESET_SUCCESS, payload: result });
  } catch (e) {
    yield put({ type: PW_RESET_FAILURE, error: true, payload: e });
  }
}

function* confirmResetPassword(action: ConfirmResetPasswordAction) {
  try {
    const result: PasswordResetConfirm = yield call(
      ApiService.destroyPasswordResetConfirm,
      {
        requestBody: action.payload.newPasswordDetails,
      }
    );
    yield put({ type: PW_RESET_CONFIRM_SUCCESS, payload: result });
    action.payload.navigate("/"); // Sends them to the login page
    const id = nextId();
    yield put(
      addToast(
        {
          kind: "success",
          title: "Changed Password",
          message: "Password changed successfully",
        },
        id
      )
    );
    yield delay(10000);
    yield put(removeToast(id));
  } catch (e: any) {
    const { status } = e;
    if (status >= 400 && status < 500) {
      const passwordIssues = extractPasswordIssues(e);
      if (passwordIssues.length > 0) {
        yield put({
          type: PW_RESET_CONFIRM_FAILURE,
          error: false,
          payload: { passwordIssues },
        });
        return;
      }
    }
    yield put({ type: PW_RESET_CONFIRM_FAILURE, error: true, payload: e });
  }
}

function* authorizedChangePassword(
  payload: PasswordChange,
  onSuccess?: Function
) {
  try {
    const result: PasswordChange = yield call(
      ApiService.destroyPasswordChange,
      {
        requestBody: payload,
      }
    );

    const id = nextId();
    yield all([
      put(
        addToast(
          {
            kind: "success",
            title: "Changed Password",
            message: "Password changed successfully.",
          },
          id
        )
      ),
      put({ type: AUTHORIZED_SET_PASSWORD_SUCCESS, payload: result }),
    ]);
    if (onSuccess) {
      onSuccess();
    }
    yield delay(10000);
    yield put(removeToast(id));
  } catch (e: any) {
    const passwordIssues = extractPasswordIssues(e);
    const newToastId = nextId();
    if (passwordIssues.length > 0) {
      // Able to pull intelligible errors out about the password
      yield all(
        passwordIssues.map((passwordIssue) =>
          put(
            addToast(
              {
                kind: "danger",
                title: "Error Changing Password",
                message: passwordIssue,
              },
              newToastId
            )
          )
        )
      );
      yield delay(10000);
      yield put(removeToast(newToastId));
      return;
    }
    yield all([
      put(
        addToast(
          {
            kind: "danger",
            title: "Error changing password",
            message: e.message || "Could not change password.",
          },
          newToastId
        )
      ),
      put({
        type: AUTHORIZED_SET_PASSWORD_FAILURE,
        error: true,
        payload: e,
      }),
    ]);
    yield delay(10000);
    yield put(removeToast(newToastId));
  }
}

////////////////////////
// User Functionality //
////////////////////////

function* userDetail(action: { type: string; payload: { id: number } }) {
  try {
    const user: User = yield call(ApiService.retrieveUser, action.payload);
    yield put({ type: USER_DETAIL_SUCCESS, payload: user });
  } catch (e) {
    yield put({ type: USER_DETAIL_FAILURE, error: true, payload: e });
  }
}

function* setProfile(payload: any, onSuccess?: Function) {
  try {
    const user: User = yield call(ApiService.partialUpdateUser, payload);
    const id: number = nextId();
    yield all([
      put(
        addToast(
          {
            kind: "success",
            title: "Saved Profile",
            message: "Profile saved successfully.",
          },
          id
        )
      ),
      put({
        type: SET_PROFILE_SUCCESS,
        payload: user,
        key: "user/me/update",
        requestSuccess: true,
      }),
    ]);
    if (onSuccess) {
      onSuccess();
    }
    yield delay(10000);
    yield put(removeToast(id));
  } catch (e: any) {
    const id: number = nextId();
    if (e.body) {
      yield put({
        type: SET_PROFILE_FAILURE,
        error: false,
        payload: e,
        requestKey: "user/me/update",
        requestFailure: true,
      });
    } else {
      yield all([
        put(
          addToast(
            {
              kind: "danger",
              title: "Error Saving Profile",
              message: "Could not save profile.",
            },
            id
          )
        ),
        put({
          type: SET_PROFILE_FAILURE,
          error: false,
          payload: e,
          requestKey: "user/me/update",
          requestFailure: true,
        }),
      ]);
      yield delay(10000);
      yield put(removeToast(id));
    }
  }
}

function* saveProfile(action: {
  type: string;
  payload: { user: User; passwordChange: PasswordChange; onSuccess: Function };
}) {
  const { user, passwordChange, onSuccess } = action.payload;
  const oldUser: User = yield select(
    ({ auth }: { auth: AuthState }) => auth.user
  );
  const calls = [];
  const passwordFieldsBlank =
    !passwordChange.new_password1 && !passwordChange.new_password2;
  if (!isEqual(oldUser, user)) {
    calls.push(
      call(
        setProfile,
        { id: oldUser.id, requestBody: user },
        passwordFieldsBlank ? onSuccess : undefined
      )
    );
  }
  if (!passwordFieldsBlank) {
    calls.push(call(authorizedChangePassword, passwordChange, onSuccess));
  }
  if (calls.length) {
    yield all(calls);
  } else {
    yield put({
      type: SET_PROFILE_SKIP,
      requestKey: "user/me/update",
      requestSkip: true,
    });
  }
}

export function* authSaga() {
  // Login Functionality
  yield takeLatest(LOGIN_REQUEST, logIn);
  yield takeLatest(LOGIN_BARCODE, loginWithToken);

  // JWT Token Functionality
  yield takeLatest(TOKEN_REQUEST, refreshAccessToken);
  yield takeLatest(TOKEN_PAIR_REQUEST, refreshAccessTokenPair);

  // MFA Functionality
  yield takeLatest(MFA_LOGIN_GET_CODE_REQUEST, mfaLoginGetCode);
  yield takeLatest(MFA_GET_CODE_REQUEST, mfaGetCode);
  yield takeLatest(MFA_VERIFY_CODE_REQUEST, mfaVerifyCode);

  // Password Reset Functionality
  yield takeLatest(PW_RESET_REQUEST, resetPassword);
  yield takeLatest(PW_RESET_CONFIRM_REQUEST, confirmResetPassword);

  // User Functionality
  yield takeLatest(USER_DETAIL_REQUEST, userDetail);
  yield takeLatest(SAVE_PROFILE_REQUEST, saveProfile);
}
